import type { VerificationTranslations } from "@/shared/lang/interfaces/verification";

import type { AccountCapabilityKey, KycStatus, VerificationNextStep } from "../types/verification-api.types";
import type { KybScreen, KycScreen, VerificationResult } from "../types/verification.types";

// ── Backend endpoints ──
// Bare paths: `VITE_COLOCA_API` already includes the `/api/v1` global prefix, and
// the controllers are mounted under `public/` (identity token) and `internal/`
// (JWT) via NestJS RouterModule. Mirrors `IDENTITIES_API_ENDPOINTS` in the cards module.
const PUBLIC_BASE = "/public/identities/verification";
const INTERNAL_IDENTITIES = "/internal/identities";
const INTERNAL_VERIFICATION = `${INTERNAL_IDENTITIES}/verification`;

export const VERIFICATION_API_ENDPOINTS = {
  // Public flow (marco B · @IdentityTokenAuth)
  PUBLIC_SESSION: `${PUBLIC_BASE}/session`,
  PUBLIC_CONSENT: `${PUBLIC_BASE}/consent`,
  PUBLIC_CAPTURE_START: `${PUBLIC_BASE}/capture/start`,
  PUBLIC_STATUS: `${PUBLIC_BASE}/status`,
  PUBLIC_LINK_REFRESH: `${PUBLIC_BASE}/link/refresh`,
  // Authenticated flow (marcos A/C · @JwtAuth)
  CREATE_PERSON: `${INTERNAL_VERIFICATION}/person`,
  CREATE_BUSINESS: `${INTERNAL_VERIFICATION}/business`,
  INTERNAL_STATUS: `${INTERNAL_VERIFICATION}/status`,
  INTERNAL_CAPTURE_START: (identityId: string) => `${INTERNAL_VERIFICATION}/${identityId}/capture/start`,
  ISSUE_LINK: (identityId: string) => `${INTERNAL_VERIFICATION}/${identityId}/kyc-link`,
  DOCUMENT: (identityId: string) => `${INTERNAL_IDENTITIES}/${identityId}/document`,
  ORGANIZATION_IDENTITY_STATUS: (organizationId: string) =>
    `${INTERNAL_IDENTITIES}/organization/${organizationId}/status`,
  // The organization's own verification (KYC or KYB) and the capabilities it unlocks (JWT)
  ACCOUNT_VERIFICATION: "/internal/account/verification",
  ACCOUNT_IDENTITY_CHANGE_REQUEST: "/internal/account/verification/identity-change-request",
  ACCOUNT_IDENTITY_CHANGE_WAIVER: "/internal/account/verification/identity-change-waiver",
} as const;

/** Public route of the front served by the resumable link (marco B): `/verify/:token`. */
export const VERIFICATION_LINK_ROUTE = "/verify";

/** Backoff schedule for polling `GET .../verification/status` when no socket push is available. */
export const STATUS_POLL_INTERVALS_MS = [3000, 5000, 8000, 13000, 21000] as const;

// ── Account verification (the organization's own KYC/KYB) ──

/**
 * How long a read of `GET /internal/account/verification` is trusted before the next mount re-reads it. Short
 * on purpose — it blocks money — and the onboarding flow and a payouts 403 invalidate it explicitly anyway.
 */
export const ACCOUNT_VERIFICATION_STALE_AFTER_MS = 60_000;

/** Render order of the capability matrix. */
export const ACCOUNT_CAPABILITY_KEYS: readonly AccountCapabilityKey[] = [
  "payouts.create",
  "payins.create",
  "wallets.recharge",
  "swaps.create",
  "cards.issue",
];

/** Label of each capability, per wire key. */
export const CAPABILITY_LABEL_KEYS: Record<AccountCapabilityKey, keyof VerificationTranslations> = {
  "payouts.create": "capability_payouts_create",
  "payins.create": "capability_payins_create",
  "wallets.recharge": "capability_wallets_recharge",
  "swaps.create": "capability_swaps_create",
  "cards.issue": "capability_cards_issue",
};

/** CTA per next step. `wait_review` and `none` deliberately have none — there is nothing the user can do. */
export const NEXT_STEP_CTA_KEYS: Partial<Record<VerificationNextStep, keyof VerificationTranslations>> = {
  create_corporate_identity: "banner_cta",
  complete_capture: "banner_cta_resume",
  retry: "result_rejected_cta",
};

/** Where every CTA above lands: the onboarding engine, which resumes from the corporate identity. */
export const ONBOARDING_VERIFICATION_ROUTE = "/app/onboarding/verification";

/** INTERNAL staff surface: read any organization's verification and lift its identity-change cooldown. */
export const ACCOUNT_VERIFICATION_ADMIN_ROUTE = "/app/account-verification";

// ── Stepper model (5 steps for both flows) ──
export const VERIFICATION_TOTAL_STEPS = 5;

/** I18n keys for the stepper labels, per purpose. */
export const KYC_STEP_LABEL_KEYS: ReadonlyArray<keyof VerificationTranslations> = [
  "step_intro",
  "step_consent",
  "step_data",
  "step_capture",
  "step_result",
];

export const KYB_STEP_LABEL_KEYS: ReadonlyArray<keyof VerificationTranslations> = [
  "step_intro",
  "step_consent",
  "step_company",
  "step_capture",
  "step_result",
];

/** Maps each KYC screen to its zero-based stepper index (Appendix B). */
export const KYC_STEP_BY_SCREEN: Record<KycScreen, number> = {
  intro: 0,
  consent: 1,
  data: 2,
  confirm: 2,
  "capture-doc": 3,
  processing: 4,
  result: 4,
  expired: 4,
  already: 4,
  mobile: 3,
  invalid: 0,
};

/** Maps each KYB screen to its zero-based stepper index (Appendix B). */
export const KYB_STEP_BY_SCREEN: Record<KybScreen, number> = {
  intro: 0,
  consent: 1,
  company: 2,
  "company-verified": 2,
  capture: 3,
  submit: 3,
  processing: 4,
  result: 4,
  documents: 4,
  "docs-empty": 4,
};

// ── Welcome after approval ──
// The approved result no longer auto-advances: it offers a choice of where to go, and carrying the reader away
// from a menu they are still reading would take the choice from them.

/** `localStorage` prefix of the "welcome already shown" marks, one per user and corporate identity. */
export const VERIFICATION_WELCOME_STORAGE_PREFIX = "coloca:verification-welcome:";

/**
 * How recent an approval must be for the dashboard to announce it. The mark lives in one browser, so an
 * organization approved months ago would otherwise be congratulated on every new device.
 */
export const VERIFICATION_WELCOME_FRESH_DAYS = 14;

/** The welcome offers at most this many shortcuts: a choice, not a second sidebar. */
export const VERIFICATION_WELCOME_MAX_SHORTCUTS = 6;

/** Maps the backend `KycStatus` to the design's terminal result. `PENDING` has no result yet. */
export const RESULT_BY_KYC_STATUS: Partial<Record<KycStatus, VerificationResult>> = {
  VERIFIED: "verified",
  IN_REVIEW: "review",
  REJECTED: "rejected",
  EXPIRED: "expired",
};

// ── Rejection reasons ──
// The backend emits `rejection_reason` either as a stable Coloca i18n key
// (`verification.rejection.<cause>`) or, for identities rejected before the i18n
// migration, as legacy raw provider text. The front i18n uses flat snake_case
// keys (`rejection_<cause>`); this map bridges the known keys. A reason NOT in
// the map is legacy raw text and must be shown as-is — never collapsed to
// "generic". `resolveRejectionDescription` is the single implementation of that
// contract; consumers must not reimplement the lookup.
const DEFAULT_REJECTION_KEY: keyof VerificationTranslations = "rejection_generic";

const REJECTION_REASON_KEY_MAP: Record<string, keyof VerificationTranslations> = {
  "verification.rejection.document_quality": "rejection_document_quality",
  "verification.rejection.document_invalid": "rejection_document_invalid",
  "verification.rejection.document_incomplete": "rejection_document_incomplete",
  "verification.rejection.selfie_mismatch": "rejection_selfie_mismatch",
  "verification.rejection.data_mismatch": "rejection_data_mismatch",
  "verification.rejection.duplicate": "rejection_duplicate",
  "verification.rejection.compliance": "rejection_compliance",
  "verification.rejection.additional_info_required": "rejection_additional_info_required",
  "verification.rejection.generic": "rejection_generic",
};

/**
 * Resolves the backend `rejection_reason` to the user-facing description: known Coloca keys are translated
 * via `t`; anything else is legacy raw provider text and is returned as-is. Empty → generic.
 */
export function resolveRejectionDescription(
  reason: string | undefined,
  t: (key: keyof VerificationTranslations) => string,
): string {
  if (!reason) return t(DEFAULT_REJECTION_KEY);
  const key = REJECTION_REASON_KEY_MAP[reason];
  return key ? t(key) : reason;
}
